import React, { useEffect, useState } from "react";
import {
  Container,
  CreateContainer,
  StyledForm,
  OperationSelectorCard,
  OperationLabel,
  OperationChipsGrid,
  OperationChip,
  ChipDot,
  ActionButtonsRow,
} from "./styles";
import ActionBar from "../../common/ActionBar";
import { Formik } from "formik";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage, getFileName } from "../../../helpers/functions";
import AWSService from "../../../services/AWSService";
import {
  PrimaryOutlinedCTAButton,
  PrimaryCTAButton,
} from "../../common/Buttons";
import UploadNotice from "./component/UploadNotice";
import GenerateNotice from "./component/GenerateNotice";
import { NoticeGenerationSchema } from "../../../helpers/validationSchema";
import NoticeService from "../../../services/NoticeService";
import HeaderFooter from "./component/HeaderFooter";
import { navigate } from "@reach/router";
import UpdateNotice from "./component/UpdateNotice";
import NoticeDesign from "./component/NoticeDesign";
import useUser from "../../../hooks/useUser";
import ValidationErrors from "./component/ValidationError";
import AddSignature from "./component/AddSign";
import TemplateDelete from "./component/TemplateDelete";
import CaseService from "../../../services/CaseService";
import Preview from "../FinalApprover/component/Preview";
import ResponseModal from "../../pages/AssignNeutralBulkCase/ResponseModal";

const NoticeGeneration = () => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [menuNotice, setMenuNotice] = useState([]);
  const [menuTemplateCode, setMenuTemplateCode] = useState([]);
  const [menuHeaderFooter, setMenuHeaderFooter] = useState([]);
  const [letterHeadArr, setLetterHeaderArr] = useState([]);
  const [find, setFind] = useState(false);
  const [letterMenuItems, setLetterMenuItems] = useState([]);
  const [triggerNotify, setTriggerNotify] = useState(true);
  const [noticeDesign, setNoticeDesign] = useState({});
  const [noticeDesignTemplate, setNoticeDesignTemplate] = useState({});
  const [selectedPartyName, setSelectedPartyName] = useState("");
  const { userObject } = useUser();
  const [modal, setModal] = useState({ state: false });
  const [previewModal, setPreviewModal] = useState({ state: false });
  const [showResponseModal, setShowResponseModal] = useState({
    state: false,
  });
  const [bulkReviewModal, setBulkReviewModal] = useState({ state: false });
  const [options, setOptions] = useState(
    userObject?.isSuperCaseManager ? 1 : 2,
  );
  const [formResetKey, setFormResetKey] = useState(0);

  useEffect(() => {
    setOptions(userObject?.isSuperCaseManager ? 1 : 2); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userObject]);

  async function getNoticeTemplates(partyId) {
    try {
      setLoader({ state: true, message: "fetching notice details..." });
      const response = await NoticeService.getNoticeTemplates(partyId);
      if (response[0]) {
        const caseManagerResponse = await CaseService.getCMlist(
          "?page=1&perPage=1000&role=caseManager",
        );
        const caseManagers = caseManagerResponse?.data || [];
        const codeAndTemplates = Object.keys(
          response[0]?.codeAndTemplates || {},
        ).reduce((categories, category) => {
          categories[category] = response[0].codeAndTemplates[category].map(
            (template) => {
              let history = [];
              try {
                history = JSON.parse(template?.history || "[]");
              } catch (_) {}
              const logs = history.reduce(
                (allLogs, item) => [
                  ...allLogs,
                  ...(item?.logs || []).map((log) => ({
                    ...log,
                    date: item.date,
                  })),
                ],
                [],
              );
              const latestLog = logs.reduce(
                (latest, log) =>
                  !latest || new Date(log.loggedAt) > new Date(latest.loggedAt)
                    ? log
                    : latest,
                null,
              );
              const caseManager = caseManagers.find(
                (manager) => String(manager?.id) === String(latestLog?.ownerId),
              );
              return {
                ...template,
                historyDisplay: latestLog
                  ? `${caseManager?.name || "Unknown Case Manager"} / ${latestLog.time || "-"} / ${latestLog.date || latestLog.loggedAt?.slice(0, 10) || "-"}`
                  : "No history",
              };
            },
          );
          return categories;
        }, {});
        setMenuNotice(
          response[0]?.noticeCategory?.map((item) => ({
            label: item,
            value: item,
          })),
        );
        setMenuTemplateCode(codeAndTemplates);
        const arrayOfObject = response[0]?.letterHeadArr.map((item) => ({
          label: item,
          value: item,
        }));
        setMenuHeaderFooter([{ label: "None", value: "" }, ...arrayOfObject]);
        setLetterHeaderArr(response[0]?.letterHeadOption);
        setNoticeDesignTemplate(response[0]?.designData?.designData);
      }
    } catch (err) {
      enqueueSnackbar(err?.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  async function getNoticeTemplate(templateId) {
    if (!templateId) return;

    try {
      setLoader({ state: true, message: "fetching template details..." });
      const response = await NoticeService.getNoticeTemplate(templateId);
      const payload = response?.data || response;
      const rawHistory =
        payload?.history ||
        payload?.templateHistory ||
        payload?.noticeTemplateHistory ||
        response?.history ||
        (Array.isArray(payload)
          ? payload
          : payload?.ownerId || payload?.loggedAt || payload?.time
            ? [payload]
            : []);
      let history = rawHistory;
      if (typeof history === "string") {
        try {
          history = JSON.parse(history);
        } catch (_) {
          history = [];
        }
      }
      const template = response?.data || response?.template || response;
      if (template?.id || template?.templateCode) {
        setMenuTemplateCode((categories) => {
          const nextCategories = { ...categories };
          Object.keys(nextCategories).forEach((category) => {
            nextCategories[category] = nextCategories[category]?.map((item) =>
              String(item.id) === String(templateId)
                ? { ...item, ...template, id: item.id }
                : item,
            );
          });
          return nextCategories;
        });
      }
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  function handlePartySelect(partyLabel, partyId) {
    setSelectedPartyName(partyLabel);
    getNoticeTemplates(partyId);
  }

  function openTemplatePreview(template) {
    if (!template?.noticeTemplate) return;
    setPreviewModal({
      state: true,
      url: template.noticeTemplate,
      name: template.templateCode,
      format: "docx",
    });
  }

  async function getNoticeDesign(partyId) {
    try {
      setLoader({ state: true, message: "fetching notice details..." });
      const response = await NoticeService.getNoticeDesign(partyId);
      if (Object.keys(response?.designData || {}).length) {
        setNoticeDesign({ partyId: partyId, ...response?.designData });
      }
    } catch (err) {
      enqueueSnackbar(err?.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const checkbox_actions = [
    {
      label: "Create",
      name: "create",
      value: 1,
      icon: "📄",
      hidden: userObject?.isSuperCaseManager ? false : true || true,
    },
    {
      label: "Update",
      name: "update",
      value: 4,
      icon: "✏️",
      hidden: userObject?.isSuperCaseManager ? false : true || true,
    },
    { label: "Generate", name: "generate", value: 2, icon: "⚡" },
    {
      label: "Add Letter Head",
      name: "headerFooterOptions",
      value: 3,
      icon: "🖨️",
    },
    { label: "Notice Design", name: "noticeDesign", value: 5, icon: "🎨" },
    { label: "Add Signature", name: "addSignature", value: 6, icon: "✍️" },
    {
      label: "Delete",
      name: "templatedCodeDelete",
      value: 7,
      icon: "🗑️",
      hidden: userObject?.isSuperCaseManager ? false : true || true,
    },
  ];

  const inputs = [
    {
      type: "text",
      name: "noticeCategory",
      label: "Notice Category",
      required: true,
    },
    {
      type: "text",
      name: "templateCode",
      label: "Template Code",
      required: true,
    },
  ];

  const radio_inputs = [
    { label: "Image", name: "image", value: "image" },
    { label: "Text", name: "text", value: "text" },
  ];

  const radio_type_inputs = [
    { label: "Excel", name: "excel", value: "excel" },
    { label: "Case Ids", name: "caseIds", value: "caseIds" },
    {
      label: "Contract Numbers",
      name: "contractNumbers",
      value: "contractNumbers",
    },
    { label: "Lot Number", name: "lotNumber", value: "lotNumber" },
  ];

  const case_id_inputs = [
    {
      label: "From CaseId",
      name: "fromCaseId",
      type: "number",
      required: true,
    },
    { label: "To CaseId", name: "toCaseId", type: "number", required: true },
    {
      type: "text",
      name: "exceptCaseIds",
      label: "Except Case IDs (comma separated)",
      required: false,
      placeholder: "e.g., 102, 109, 120",
      helperText: "Optional: exclude specific cases from the selected range",
    },
  ];

  const upload_templates = [
    {
      name: "noticeTemplate",
      label: "Upload Notice Template (docx)",
      type: "upload",
      required: true,
      accept: ".docx",
      types: [
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
    },
    {
      name: "excelTemplate",
      label: "Upload Excel Template (xlsx)",
      type: "upload",
      required: true,
      accept: ".xlsx",
      types: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ],
    },
  ];

  async function s3Upload(file, field, setFieldValue, fileTypes) {
    if (file) {
      try {
        const types = fileTypes;
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const errorMessage = "Invalid File Type";
        if (!types.includes(file?.type)) throw errorMessage;
        const payload = { key: file.name };
        /*
        const maxSize = 3 * 1024 * 1024;
        if (file.size > maxSize) {
          enqueueSnackbar("File size must be less than or equal to 3MB", {
            variant: "error",
          });
          return;
        }
        */
        let fileNameWithoutExt = file.name.replace(
          /\s*\.(xlsx|pdf|docx)$/i,
          "",
        );
        const hasSpecialChar = /[^a-zA-Z0-9\s-_]/.test(fileNameWithoutExt);
        if (hasSpecialChar) {
          enqueueSnackbar("Filename contains invalid special characters", {
            variant: "error",
          });
        } else {
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
          }
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "please wait..." });
      if (values?.noticeOptions === 1) {
        const payload = {
          partyId: values?.partyId,
          noticeCategory: values?.noticeCategory,
          templateCode: values?.templateCode,
          excelTemplate: values?.excelTemplate,
          noticeTemplate: values?.noticeTemplate,
        };
        const response = await NoticeService.uploadNoticeTemplates(payload);
        if (response) {
          enqueueSnackbar(response?.message, { variant: "success" });
          resetForm();
        }
        setOptions(values?.noticeOptions);
      } else if (values?.noticeOptions === 2) {
        let noticeDesignData =
          values?.noticeCopyDesign === "default"
            ? {
                lineSpacing: values?.lineSpacing,
                afterLineSpacing: values?.afterLineSpacing,
                topMargin: values?.topMargin,
                bottomMargin: values?.bottomMargin,
                leftMargin: values?.leftMargin,
                rightMargin: values?.rightMargin,
                headerDistance: values?.headerDistance,
                footerDistance: values?.footerDistance,
                fontSize: values?.fontSize,
              }
            : noticeDesignTemplate;

        const source =
          values?.letterHeadName &&
          letterHeadArr[values?.letterHeadName]?.length &&
          letterHeadArr[values?.letterHeadName][0];
        let letterHeadPayload;
        if (source) {
          if (source.letterHeadType === "image") {
            letterHeadPayload = {
              id: source.id,
              headerImage: source.headerImage || "",
              footerImage: source.footerImage || "",
              letterHeadType: "image",
            };
          } else if (source.letterHeadType === "text") {
            letterHeadPayload = {
              id: source.id,
              letterHeadType: "text",
              headerFooterText: {
                headerText: source.headerFooterText?.headerText || [],
                footerText: source.headerFooterText?.footerText || [],
              },
              headerAlignment: values?.headerAlignment || "left",
              footerAlignment: values?.footerAlignment || "left",
            };
          }
        } else {
          letterHeadPayload = { headerImage: "", footerImage: "" };
        }

        let noticeTemplatePayload = menuTemplateCode[
          values?.noticeCategory
        ].filter(
          (template) => template?.templateCode === values?.templateCode,
        )[0]?.noticeTemplate;

        const payload = {
          partyId: values?.partyId,
          noticeCategory: values?.noticeCategory,
          templateCode: values?.templateCode,
          lotNo: values?.lotNo || values?.lotNumber,
          noticeTemplate: noticeTemplatePayload,
          letterHeadName: values?.letterHeadName,
          letterHeadImages: letterHeadPayload,
          noticeDesign: noticeDesignData,
          codeType: values?.codeType,
          arbitratorName: values?.arbitratorName,
          institutionName: values?.institutionName,
          loanType: values?.loanType,
          loanStatus: values?.loanStatus,

          ...(values?.typeOfSelect === "excel" && {
            excelFile: values?.excelFile,
          }),

          ...(values?.typeOfSelect === "contractNumbers" && {
            contractNumbers: values?.contractNumbers,
          }),

          ...(values?.typeOfSelect === "caseIds" && {
            fromCaseId: values?.fromCaseId,
            toCaseId: values?.toCaseId,
            exceptCaseIds: values?.exceptCaseIds
              ? values.exceptCaseIds
                  .split(",")
                  .map((item) => Number(item.trim()))
              : [],
          }),

          ...(values?.typeOfSelect === "lotNumber" && {
            lotNumber: values?.lotNo || values?.lotNumber,
            exceptCaseIds: values?.exceptCaseIds
              ? values.exceptCaseIds
                  .split(",")
                  .map((item) => Number(item.trim()))
              : [],
          }),
        };

        /*

        if (values?.typeOfSelect === "excel") {
          const res = await Axios.post(
            "https://mod.justact.co.in/check-keyerror",
            {
              noticeTemplate: noticeTemplatePayload,
              excelFile: values?.excelFile,
            },
          );

          if (!res?.data?.message) {
            setModal({
              state: true,
              metaData: res.data,
            });

            return;
          }
        }
        
        */

        const response = await NoticeService.bulkNoticeGenerate(payload);

        const bulkReview = response?.reports
          ? response
          : response?.data?.reports
            ? response.data
            : null;

        if (bulkReview?.reports) {
          setBulkReviewModal({ state: true, data: bulkReview, payload });
          return;
        }

        const datatypeError = response?.error || response?.data?.error;

        if (datatypeError?.previewExcel || datatypeError?.datatypeErrors) {
          enqueueSnackbar(response?.message, {
            variant: "error",
          });
          setShowResponseModal({
            state: true,
            datatypeErrors: datatypeError.datatypeErrors,
            errorExcel: datatypeError.errorExcel,
            previewExcel: datatypeError.previewExcel,
          });
          return;
        }

        if (response?.error?.missingKeys?.length > 0) {
          setModal({
            state: true,
            metaData: response?.error,
          });
          return;
        }

        if (response?.message) {
          enqueueSnackbar(response?.message, {
            variant: "success",
          });

          resetForm();
        }
        setOptions(values?.noticeOptions);
      } else if (values?.noticeOptions === 3) {
        const payload = {
          letterHeadName: values?.letterHeadName,
          headerImage: values?.headerImage,
          footerImage: values?.footerImage,
          typeOfLetterHead: values?.typeOfLetterHead,
          headerText: values?.headerText,
          footerText: values?.footerText,
        };
        if (find) {
          const response = await NoticeService.addLetterHeadOption(payload);
          if (response?.message) {
            enqueueSnackbar(response?.message, { variant: "success" });
            setTriggerNotify(true);
            resetForm();
          }
          setOptions(values?.noticeOptions);
        } else {
          let id = await letterMenuItems.filter(
            (item) => values?.letterHeadName === item.label,
          )[0]?.id;
          const response = await NoticeService.updateLetterHeadOption(
            payload,
            id,
          );
          if (response?.message) {
            enqueueSnackbar(response?.message, { variant: "success" });
            resetForm();
          }
          setOptions(values?.noticeOptions);
        }
      } else if (values?.noticeOptions === 4) {
        const payload = {
          noticeCategory: values?.noticeCategory,
          templateCode: values?.templateCode,
          excelTemplate: values?.excelTemplate,
          noticeTemplate: values?.noticeTemplate,
        };
        let id = await menuTemplateCode[values?.noticeCategory].filter(
          (templateCode) => templateCode?.templateCode === values?.templateCode,
        )[0]?.id;
        const response = await NoticeService.updateNoticeTemplates(payload, id);
        if (response) {
          enqueueSnackbar(response?.message, { variant: "success" });
          resetForm();
        }
        setOptions(values?.noticeOptions);
      } else if (values?.noticeOptions === 5) {
        const payload = {
          designData: {
            lineSpacing: values?.lineSpacing,
            afterLineSpacing: values?.afterLineSpacing,
            topMargin: values?.topMargin,
            bottomMargin: values?.bottomMargin,
            leftMargin: values?.leftMargin,
            rightMargin: values?.rightMargin,
            headerDistance: values?.headerDistance,
            footerDistance: values?.footerDistance,
            fontSize: values?.fontSize,
          },
        };
        setNoticeDesign({
          partyId: values?.partyId,
          lineSpacing: values?.lineSpacing,
          afterLineSpacing: values?.afterLineSpacing,
          topMargin: values?.topMargin,
          bottomMargin: values?.bottomMargin,
          leftMargin: values?.leftMargin,
          rightMargin: values?.rightMargin,
          headerDistance: values?.headerDistance,
          footerDistance: values?.footerDistance,
          fontSize: values?.fontSize,
        });
        const response = await NoticeService.updateNoticeDesign(
          values?.partyId,
          payload,
        );
        if (response) {
          enqueueSnackbar(response?.message, { variant: "success" });
          resetForm();
        }
        setOptions(values?.noticeOptions);
      } else if (values?.noticeOptions === 6) {
        let formData = new FormData();
        formData.append("files", values?.addSignature);
        const response = await fetch(
          `https://staging-api.justact.co.in/upload-signature`,
          { method: "POST", body: formData },
        );
        if (response.ok) {
          const data = await response.json();
          enqueueSnackbar(data.message, { variant: "success" });
          resetForm();
        }
      } else if (values?.noticeOptions === 7) {
        let id = menuTemplateCode[values?.noticeCategory]?.filter(
          (template) => template?.templateCode === values?.templateCode,
        )[0]?.id;

        if (!id) {
          enqueueSnackbar("Please select a valid template to delete", {
            variant: "error",
          });
          return;
        }
        const payload = {
          partyId: values?.partyId,
          noticeCategory: values?.noticeCategory,
          templateCode: values?.templateCode,
          deleteFlag: true,
        };
        const response = await NoticeService.deleteNoticeTemplate(payload, id);
        if (response?.message) {
          enqueueSnackbar("Notice template deleted successfully", {
            variant: "success",
          });
          resetForm();
        }
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
       const datatypeError =
        error?.error || error?.data?.error || error?.response?.data?.error;
      if (datatypeError?.previewExcel || datatypeError?.datatypeErrors) {
        setShowResponseModal({
          state: true,
          datatypeErrors: datatypeError.datatypeErrors,
          errorExcel: datatypeError.errorExcel,
          previewExcel: datatypeError.previewExcel,
        });
      }
    } finally {
      setLoader({ state: false });
      setFind(false);
    }
  }

  const getSubmitLabel = (noticeOptions, find) => {
    if (noticeOptions === 1) return "Upload";
    if (noticeOptions === 2) return "Generate Notice";
    if (noticeOptions === 5) return "Create Design";
    if (noticeOptions === 7) return "Delete";
    if (noticeOptions === 3) return find ? "Add" : "Update";
    return "Update";
  };

  return (
    <>
      <ActionBar breadcrumbs={["Notice Generation"]} />
      <Container>
        <CreateContainer className="CreateCase" />
        <Formik
          initialValues={{
            noticeOptions: options || "",
            noticeCategory: "",
            templateCode: "",
            letterHeadName: "",
            lotNo: "",
            noticeTemplate: "",
            excelTemplate: "",
            partyId: noticeDesign?.partyId || "",
            excelFile: "",
            headerImage: "",
            footerImage: "",
            noticeCopyDesign: "",
            lineSpacing: noticeDesign?.lineSpacing || 10,
            afterLineSpacing: noticeDesign?.afterLineSpacing || 1,
            topMargin: noticeDesign?.topMargin || 2.25,
            bottomMargin: noticeDesign?.bottomMargin || 1.25,
            leftMargin: noticeDesign?.leftMargin || 1.5,
            rightMargin: noticeDesign?.rightMargin || 1.5,
            headerDistance: noticeDesign?.headerDistance || 0.25,
            footerDistance: noticeDesign?.footerDistance || 0.25,
            fontSize: noticeDesign?.fontSize || 10,
            codeType: "",
            arbitratorName: "",
            institutionName: "",
            loanType: "",
            loanStatus: "",
            typeOfLetterHead: "image",
            typeOfSelect: "excel",
            headerText: [""],
            footerText: [""],
            headerAlignment: 1,
            footerAlignment: 1,
            addSignature: "",
            templatedCodeDelete: "",
            fromCaseId: "",
            toCaseId: "",
            exceptCaseIds: "",
            contractNumbers: "",
            lotNumber: "",
            fileName: "",
          }}
          validationSchema={NoticeGenerationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            resetForm,
          }) => (
            <StyledForm>
              {/* ── Operation Selector ── */}
              <OperationSelectorCard>
                <OperationLabel>Select Notice Operation</OperationLabel>
                <OperationChipsGrid>
                  {checkbox_actions
                    .filter((item) => !item.hidden)
                    .map((item) => (
                      <OperationChip
                        key={item.value}
                        selected={options === item.value}
                        onClick={() => {
                          const selectedValue = item.value;
                          setOptions(selectedValue);
                          setFieldValue("noticeOptions", selectedValue);
                          if (selectedValue !== 5) {
                            setFind(false);
                            resetForm();
                            setNoticeDesign({});
                          } else {
                            setFind(false);
                            resetForm();
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="noticeOptions"
                          value={item.value}
                          checked={options === item.value}
                          onChange={() => {}}
                        />
                        <ChipDot selected={options === item.value} />
                        {item.label}
                      </OperationChip>
                    ))}
                </OperationChipsGrid>
              </OperationSelectorCard>

              {/* ── Sub-forms ── */}
              {values?.noticeOptions === 1 ? (
                <UploadNotice
                  {...{
                    values,
                    handleChange,
                    errors,
                    touched,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    inputs,
                    upload_templates,
                    s3Upload,
                    getFileName,
                  }}
                  key={formResetKey}
                  menuItems={[]}
                />
              ) : values?.noticeOptions === 2 ? (
                <GenerateNotice
                  {...{
                    values,
                    handleChange,
                    errors,
                    touched,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    inputs,
                    upload_templates,
                    s3Upload,
                    getFileName,
                    menuNotice,
                    menuTemplateCode,
                    menuHeaderFooter,
                    getNoticeTemplates,
                    case_id_inputs,
                    noticeDesignTemplate,
                    selectedPartyName,
                    onPartySelect: handlePartySelect,
                    openTemplatePreview,
                    letterHeadArr,
                    setModal,
                  }}
                  key={formResetKey}
                  cmId={userObject?.id}
                  radioInputs={radio_inputs}
                  radioTypeInputs={radio_type_inputs}
                  menuItems={[]}
                />
              ) : values?.noticeOptions === 3 ? (
                <HeaderFooter
                  {...{
                    values,
                    handleChange,
                    errors,
                    touched,
                    handleBlur,
                    setFieldValue,
                    setFind,
                    letterMenuItems,
                    setLetterMenuItems,
                    triggerNotify,
                    setTriggerNotify,
                  }}
                  radioInputs={radio_inputs}
                />
              ) : values?.noticeOptions === 4 ? (
                <UpdateNotice
                  {...{
                    values,
                    handleChange,
                    errors,
                    touched,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    inputs,
                    upload_templates,
                    s3Upload,
                    getFileName,
                    menuNotice,
                    menuTemplateCode,
                    getNoticeTemplates,
                    openTemplatePreview,
                  }}
                  key={formResetKey}
                  menuItems={[]}
                />
              ) : values.noticeOptions === 5 ? (
                <NoticeDesign
                  {...{
                    values,
                    handleChange,
                    errors,
                    touched,
                    handleBlur,
                    setFieldValue,
                    inputs,
                  }}
                  key={formResetKey}
                  menuItems={[]}
                  getNoticeTemplates={getNoticeDesign}
                />
              ) : values?.noticeOptions === 6 ? (
                <AddSignature
                  {...{
                    values,
                    errors,
                    touched,
                    setFieldValue,
                  }}
                />
              ) : values?.noticeOptions === 7 ? (
                <TemplateDelete
                  {...{
                    values,
                    handleChange,
                    errors,
                    touched,
                    handleBlur,
                    setFieldValue,
                    menuNotice,
                    menuTemplateCode,
                    menuHeaderFooter,
                    getNoticeTemplates,
                    getNoticeTemplate,
                  }}
                  key={formResetKey}
                  cmId={userObject?.id}
                  menuItems={[]}
                />
              ) : null}

              {/* ── Action Buttons ── */}
              <ActionButtonsRow>
                <PrimaryOutlinedCTAButton
                  style={{ minWidth: 120 }}
                  onClick={() => navigate(`/dashboard/case-manager`)}
                >
                  Back
                </PrimaryOutlinedCTAButton>
                <PrimaryOutlinedCTAButton
                  style={{ minWidth: 120 }}
                  onClick={() => {
                    resetForm();
                    setSelectedPartyName("");
                    setNoticeDesign({});
                    setFormResetKey((k) => k + 1);
                  }}
                >
                  Clear Form
                </PrimaryOutlinedCTAButton>
                <PrimaryCTAButton
                  style={{ minWidth: 160 }}
                  onClick={() => {
                    if (values?.noticeOptions === 7) {
                      onFormSubmit(values, { resetForm });
                    } else {
                      handleSubmit();
                    }
                  }}
                >
                  {getSubmitLabel(values?.noticeOptions, find)}
                </PrimaryCTAButton>
              </ActionButtonsRow>
            </StyledForm>
          )}
        </Formik>
      </Container>
      {/* {modal?.state && <ValidationErrors {...{ modal, setModal }} />} */}
      {modal?.state &&
        (modal.preview ? (
          <Preview {...{ modal, setModal }} />
        ) : (
          <ValidationErrors {...{ modal, setModal }} />
        ))}
      <Preview modal={previewModal} setModal={setPreviewModal} />
      <ResponseModal
        open={showResponseModal?.state}
        onClose={() => setShowResponseModal({ state: false })}
        datatypeErrors={showResponseModal?.datatypeErrors}
        errorExcel={showResponseModal?.errorExcel}
        previewExcel={showResponseModal?.previewExcel}
        title="Datatype Validation"
      />
      <ResponseModal
        open={bulkReviewModal?.state}
        onClose={() => setBulkReviewModal({ state: false })}
        bulkReview={bulkReviewModal?.data}
        onReviewConfirm={async ({ datatypeFile, headerOverrides }) => {
          try {
            setLoader({ state: true, message: "Generating notice..." });
            const s3Response = await AWSService.getS3URL({
              key: datatypeFile.name,
            });
            const datatypeFileUrl = await AWSService.uploadToS3(
              s3Response.url,
              datatypeFile,
            );
            const response = await NoticeService.bulkNoticeGenerate({
              ...bulkReviewModal?.payload,
              datatypeFile: datatypeFileUrl,
              ...(Object.keys(headerOverrides || {}).length && {
                headerOverrides,
              }),
              confirmGenerate: true,
            });
            const previewResponse =
              response?.error || response?.data?.error || response;
            if (previewResponse?.previewExcel) {
              enqueueSnackbar(
                response?.message || "Notice Generation failed",
                { variant: "error" },
              );
              setBulkReviewModal({ state: false });
              setShowResponseModal({
                state: true,
                datatypeErrors: previewResponse.datatypeErrors || [],
                errorExcel: previewResponse.errorExcel,
                previewExcel: previewResponse.previewExcel,
              });
              return;
            }
            if (response?.message) {
              enqueueSnackbar(response.message, { variant: "success" });
            }
            setBulkReviewModal({ state: false });
          } catch (error) {
            const previewResponse =
              error?.error || error?.data?.error || error?.response?.data;
            if (previewResponse?.previewExcel) {
              enqueueSnackbar(
                previewResponse?.message || "Notice Generation failed",
                { variant: "error" },
              );
              setBulkReviewModal({ state: false });
              setShowResponseModal({
                state: true,
                datatypeErrors: previewResponse.datatypeErrors || [],
                errorExcel: previewResponse.errorExcel,
                previewExcel: previewResponse.previewExcel,
              });
            } else {
              enqueueSnackbar(getErrorMessage(error), { variant: "error" });
            }
          } finally {
            setLoader({ state: false });
          }
        }}
        title="Excel Review"
      />
    </>
  );
};

export default NoticeGeneration;
